import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Camera,
  IdCard,
  PenLine,
  RefreshCw,
  Save,
  Truck,
  User,
  Warehouse,
} from "lucide-react";
import { getDeliveryConfig, updateDeliveryConfig } from "../../api/settings";
import { showToast } from "../../utils/showToast";
import { useCountry } from "../../context/CountryContext";
import { identityDocumentLabel } from "../../utils/identityDocumentLabel";
import { SectionCard } from "../ui/SectionCard";
import { Switch } from "../ui/Switch";
import { Button } from "../ui/Button";
import { Skeleton } from "../ui/Skeleton";

// ─────────────────────────────────────────────────────────────────────────────
// PickupFormConfig — Qué datos debe registrar el repartidor (o el supervisor de
// bodega) al cerrar una colecta: nombre de quien entrega, su documento, su firma
// y/o una foto de la carga.
//
// Se configura por separado para los dos orígenes que ya distingue el backend
// (`route_pickup` = Mis retiros, `warehouse_reception` = Recepción de pedidos), con
// un interruptor para compartir una sola configuración entre ambos.
//
// Autocontenido, igual que DeliveryFailedFormConfig: carga y guarda contra
// GET/PATCH /configuration/delivery, que resuelve el operador dueño desde el JWT y
// mergea POR SECCIÓN (guardar acá no toca la config de entrega fallida).
// ─────────────────────────────────────────────────────────────────────────────

// Mínimo de datos exigido por el negocio. También lo valida el backend: dejar una
// sola casilla marcada deja el retiro casi sin respaldo de quién entregó.
const MIN_FIELDS = 2;

const SOURCES = [
  {
    key: "routePickup",
    icon: Truck,
    title: "Mis retiros",
    subtitle: "Cuando el repartidor cierra un retiro en la ruta, en el comercio.",
  },
  {
    key: "warehouseReception",
    icon: Warehouse,
    title: "Recepción de pedidos",
    subtitle: "Cuando se reciben los paquetes en la bodega del operador.",
  },
];

/** Orden de presentación: identificación primero, evidencia después. */
const FIELDS = [
  {
    key: "fullName",
    icon: User,
    label: "Nombre y apellido de quien entrega",
  },
  {
    key: "document",
    icon: IdCard,
    // El label real depende del país del operador (RUT/DNI/Cédula) y se resuelve al render.
    label: null,
  },
  { key: "signature", icon: PenLine, label: "Firma de quien entrega" },
  { key: "photo", icon: Camera, label: "Foto de la carga" },
];

const DEFAULT_FIELDS = ["fullName", "document"];

/** Estado editable desde la respuesta del backend (o los defaults si no configuró nada). */
function stateFromConfig(config) {
  const pickupForm = config?.pickupForm ?? null;
  return {
    // `shared` por defecto true: es lo que espera un operador que nunca separó los flujos.
    shared: pickupForm ? pickupForm.shared !== false : true,
    routePickup: pickupForm?.routePickup?.length ? [...pickupForm.routePickup] : [...DEFAULT_FIELDS],
    warehouseReception: pickupForm?.warehouseReception?.length
      ? [...pickupForm.warehouseReception]
      : [...DEFAULT_FIELDS],
  };
}

export default function PickupFormConfig() {
  const { t } = useTranslation();
  const { countryCode } = useCountry();
  const docLabel = identityDocumentLabel(countryCode);

  const [state, setState] = useState(() => stateFromConfig(null));
  const [snapshot, setSnapshot] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    getDeliveryConfig()
      .then((res) => {
        if (!active) return;
        const next = stateFromConfig(res?.data ?? res);
        setState(next);
        setSnapshot(JSON.stringify(next));
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 403) {
          setForbidden(true);
        } else {
          // No se renderiza el formulario con defaults: con snapshot vacío, Guardar
          // quedaría habilitado y PATCHearía encima de la config real del operador.
          setLoadError(true);
          showToast(t("No se pudo cargar la configuración de retiros"), false);
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // `t` fuera de las deps a propósito: su identidad cambia con el idioma y eso
    // refetchearía la config, descartando ediciones sin aviso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const dirty = useMemo(() => JSON.stringify(state) !== snapshot, [state, snapshot]);

  const toggleField = (sourceKey, fieldKey) =>
    setState((prev) => {
      const current = prev[sourceKey] ?? [];
      const next = current.includes(fieldKey)
        ? current.filter((f) => f !== fieldKey)
        : // Se agrega respetando el orden de FIELDS, no el orden de click: así el
          // arreglo guardado se lee igual que la pantalla.
          FIELDS.map((f) => f.key).filter((k) => k === fieldKey || current.includes(k));
      return { ...prev, [sourceKey]: next };
    });

  // Los orígenes que el operador está editando de verdad. Con `shared`, la recepción
  // usa la lista del retiro y su bloque no se muestra.
  const visibleSources = state.shared ? SOURCES.slice(0, 1) : SOURCES;

  const errors = useMemo(() => {
    const out = {};
    for (const source of visibleSources) {
      if ((state[source.key] ?? []).length < MIN_FIELDS) {
        out[source.key] = t("Elige al menos {{n}} datos.", { n: MIN_FIELDS });
      }
    }
    return out;
  }, [state, visibleSources, t]);

  const hasErrors = Object.keys(errors).length > 0;

  const handleSave = async () => {
    if (hasErrors) {
      showToast(t("Elige al menos {{n}} datos en cada flujo.", { n: MIN_FIELDS }), false);
      return;
    }
    setSaving(true);
    try {
      // `warehouseReception` se manda SIEMPRE, incluso compartiendo: así al separar los
      // flujos el operador recupera lo que tenía en vez de empezar de cero. El backend
      // aplica el `shared` al resolver lo que ve la app.
      await updateDeliveryConfig({
        pickupForm: {
          shared: state.shared,
          routePickup: state.routePickup,
          warehouseReception: state.warehouseReception,
        },
      });
      setSnapshot(JSON.stringify(state));
      showToast(t("Datos del cierre de colecta guardados"), true);
    } catch {
      showToast(t("No se pudo guardar la configuración"), false);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (snapshot) setState(JSON.parse(snapshot));
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <SectionCard
        icon={<AlertTriangle className="w-4 h-4" />}
        title={t("Sin configuración disponible")}
        subtitle={t(
          "Tu usuario no pertenece a un operador con configuración de entrega.",
        )}
      />
    );
  }

  if (loadError) {
    return (
      <SectionCard
        icon={<AlertTriangle className="w-4 h-4" />}
        title={t("No se pudo cargar la configuración")}
        subtitle={t("Reintenta en un momento.")}
        action={
          <Button
            variant="outline"
            onClick={() => setReloadKey((k) => k + 1)}
            iconLeft={<RefreshCw size={16} />}
          >
            {t("Reintentar")}
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon={<Truck className="w-4 h-4" />}
        title={t("Datos al cerrar una colecta")}
        subtitle={t(
          "Lo que el repartidor debe registrar de quien entrega los paquetes. Se le pide en la app al terminar el retiro o la recepción.",
        )}
      >
        <Switch
          checked={state.shared}
          onChange={(next) => setState((prev) => ({ ...prev, shared: next }))}
          disabled={saving}
          label={t("Pedir lo mismo en los dos flujos")}
        />
        <p className="mt-1.5 text-xs text-fg-subtle">
          {state.shared
            ? t("La recepción en bodega pedirá lo mismo que el retiro en ruta.")
            : t("Cada flujo pide sus propios datos.")}
        </p>
      </SectionCard>

      {visibleSources.map((source) => {
        const Icon = source.icon;
        const selected = state[source.key] ?? [];
        const error = errors[source.key];
        return (
          <SectionCard
            key={source.key}
            icon={<Icon className="w-4 h-4" />}
            title={t(state.shared ? "Datos a pedir" : source.title)}
            subtitle={t(
              state.shared
                ? "Se piden tanto en Mis retiros como en Recepción de pedidos."
                : source.subtitle,
            )}
          >
            <div className="flex flex-col gap-3">
              {FIELDS.map((field) => {
                const FieldIcon = field.icon;
                const label = field.label ? t(field.label) : docLabel;
                return (
                  <div key={field.key} className="flex items-center gap-2">
                    <FieldIcon className="w-4 h-4 shrink-0 text-fg-subtle" />
                    <Switch
                      checked={selected.includes(field.key)}
                      onChange={() => toggleField(source.key, field.key)}
                      disabled={saving}
                      label={label}
                    />
                  </div>
                );
              })}
            </div>
            <div aria-live="polite">
              {error && (
                <p className="mt-3 text-xs text-status-error">{error}</p>
              )}
            </div>
          </SectionCard>
        );
      })}

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" onClick={handleReset} disabled={!dirty || saving}>
          {t("Descartar cambios")}
        </Button>
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={!dirty || saving || hasErrors}
          iconLeft={!saving ? <Save size={16} /> : undefined}
        >
          {saving ? t("Guardando…") : t("Guardar cambios")}
        </Button>
      </div>
    </div>
  );
}
